import React, {useMemo} from 'react';
import clsx from 'clsx';
import {
	faAngleLeft,
	faAngleRight,
	faTimes as TimesIcon,
	faChevronLeft as ToggleIcon,
} from '@fortawesome/pro-solid-svg-icons';
import {unstable_capitalize as capitalize} from '@mui/utils';
import {
	IconButton,
	iconButtonClasses,
	IconButtonProps,
} from '@netx/core-components/IconButton';
import {composeClasses, styled, WithClasses} from '@netx/core-components/Theme';
import {Title, type TitleProps} from '@netx/core-components/Title';
import {DrawerAnchor} from './constants';
import {
	DRAWER_TOOLBAR_CLASS_NAME,
	drawerToolbarClasses,
	DrawerToolbarClasses,
	getDrawerToolbarUtilityClass,
} from './drawerToolbarClasses';
import {DrawerActionLabel} from './locales';

export type DrawerToolbarOwnerState = WithClasses<
	DrawerToolbarClasses,
	{
		anchor: DrawerAnchor;
		cancelAsClose?: boolean;
		open?: boolean;
	}
>;

const useUtilityClasses = (ownerState: DrawerToolbarOwnerState) => {
	const {classes, anchor, open} = ownerState;
	const slots = {
		root: ['root', `anchor${capitalize(anchor)}`, open && 'open'],
		toggle: ['toggle'],
		title: ['title'],
	};
	return composeClasses(slots, getDrawerToolbarUtilityClass, classes);
};

const DrawerToolbarRoot = styled('div', {
	name: DRAWER_TOOLBAR_CLASS_NAME,
	slot: 'Root',
})(({theme, ownerState}) => ({
	display: 'flex',
	alignItems: 'center',
	justifyContent: 'flex-start',
	padding: theme.spacing(0, 1),
	zIndex: 3,
	...theme.mixins.toolbar,
	[`&.${drawerToolbarClasses.anchorLeft}`]: {
		[`& .${drawerToolbarClasses.title}`]: {
			marginRight: theme.spacing(1),
		},
	},
	[`&.${drawerToolbarClasses.anchorRight}`]: {
		[`& .${drawerToolbarClasses.title}`]: {
			marginLeft: theme.spacing(1),
		},
		// ...(!ownerState?.cancelAsClose
		// 	? {
		// 			[`& .${drawerToolbarClasses.title}`]: {
		// 				marginLeft: theme.spacing(1),
		// 			},
		// 	  }
		// 	: {
		// 			paddingLeft: theme.spacing(2),
		// 			[`& .${drawerToolbarClasses.toggle}`]: {
		// 				marginRight: theme.spacing(-1),
		// 			},
		// 	  }),
	},
	[`&:not(.${drawerToolbarClasses.open})`]: {
		[`& .${drawerToolbarClasses.title}`]: {
			visibility: 'hidden',
			margin: 0,
		},
	},
}));

const DrawerTitle = styled(Title, {
	name: DRAWER_TOOLBAR_CLASS_NAME,
	slot: 'Title',
})(({theme}) => ({
	flex: '1 1 auto',
	overflow: 'hidden',
	textOverflow: 'ellipsis',
}));

const DrawerToggle = styled(IconButton, {
	name: DRAWER_TOOLBAR_CLASS_NAME,
	slot: 'Toggle',
})(({theme}) => ({
	[`& .${iconButtonClasses.icon}`]: {
		transition: theme.transitions.create('transform'),
	},
}));

type IconButtonMethods = Required<Pick<IconButtonProps, 'onClick'>>;

export interface DrawerToolbarProps
	extends
		React.DetailedHTMLProps<
			React.HTMLAttributes<HTMLDivElement>,
			HTMLDivElement
		>,
		WithClasses<DrawerToolbarClasses> {
	actions?: React.ReactNode;
	// TODO: top and bottom are not supported just yet
	anchor: DrawerAnchor;
	cancelAsClose?: boolean;
	hideCollapseToggle?: boolean;
	hidden?: boolean;
	open?: boolean;
	secondaryTitle?: React.ReactNode;
	title?: string;
	onCollapseToggle?: IconButtonMethods['onClick'];
	TitleProps?: Partial<TitleProps>;
}

export const DrawerToolbarPrimitive = ({
	classes: _classes,
	children,
	className,

	actions,
	anchor,
	cancelAsClose,
	hidden,
	hideCollapseToggle,
	open,
	secondaryTitle,
	title = '',
	onCollapseToggle,
	TitleProps,
	...props
}: DrawerToolbarProps) => {
	const ownerState: DrawerToolbarOwnerState = {
		classes: _classes,
		anchor,
		cancelAsClose,
		open,
	};

	const classes = useUtilityClasses(ownerState);

	const Toggle = useMemo(
		() => (
			<DrawerToggle
				className={clsx(classes.toggle, iconButtonClasses.root)}
				tabIndex={hidden ? -1 : 0}
				onClick={onCollapseToggle}
				size="medium"
				icon={cancelAsClose && open ? TimesIcon : faAngleLeft}
				// edge={cancelAsClose && anchor === DrawerAnchor.RIGHT ? 'end' : undefined}
				label={
					cancelAsClose
						? DrawerActionLabel.CLOSE
						: open
							? DrawerActionLabel.COLLAPSE
							: DrawerActionLabel.UNCOLLAPSE
				}
				IconProps={{
					rotation:
						!cancelAsClose && open
							? anchor === DrawerAnchor.LEFT
								? undefined
								: 180
							: anchor === DrawerAnchor.LEFT
								? 180
								: undefined,
				}}
			/>
		),
		[classes, anchor, hidden, open, onCollapseToggle, cancelAsClose],
	);

	const Title = useMemo(
		() =>
			title ? (
				<DrawerTitle
					className={classes.title}
					variant="h5"
					primary={title}
					secondary={secondaryTitle}
					{...TitleProps}
				/>
			) : null,
		[classes, secondaryTitle, title, TitleProps],
	);

	const Actions = (open && actions) || null;

	return (
		<DrawerToolbarRoot
			ownerState={ownerState}
			className={clsx(classes.root, className)}
			{...props}
		>
			{anchor === DrawerAnchor.RIGHT ? (
				<>
					{!hideCollapseToggle && Toggle}
					{/* {cancelAsClose ? null : Toggle} */}
					{Title}
					{Actions}
					{/* {cancelAsClose ? Toggle : null} */}
				</>
			) : (
				<>
					{Actions}
					{Title}
					{!hideCollapseToggle && Toggle}
				</>
			)}
		</DrawerToolbarRoot>
	);
};

export const DrawerToolbar = React.memo(DrawerToolbarPrimitive);

export default DrawerToolbar;
